import { assert, isDefined } from '../guards'
import type { AllNullable, Nullable } from '../types'

/**
 * Python-like range
 *
 * Создает последовательность чисел в произвольном промежутке с произвольным шагом
 *
 * Варианты использования:
 * 1. `range(count)`
 *
 * Вернет множество [0...count-1]
 *
 * 2. `range(start, stop)`
 *
 * Обязательно: start <= stop
 * Вернет множество [start...stop-1]
 *
 * 3. `range(start, stop, step)`
 *
 * Обязательно: start <= stop && step > 0 || start >= stop && step < 0
 * Вернет множество от start до stop-1 с шагом step
 *
 *
 * @param startOrCount
 * @param possibleStop
 * @param step
 * @yields {number} последовательность чисел по конфигурации
 */
export function* range(
  startOrCount: number,
  possibleStop?: number,
  step = 1,
): Generator<number, void> {
  assert(step !== 0, 'Step must not to be zero')

  const start = possibleStop ? startOrCount : 0
  const stop = possibleStop ? possibleStop : startOrCount

  const isIncreasing = start <= stop && step > 0
  const isDecreasing = start >= stop && step < 0

  assert(isDecreasing || isIncreasing, 'Range must be increasing or decreasing')

  const comparator = (index: number, stop: number): boolean => {
    switch (true) {
      case isIncreasing:
        return index < stop
      case isDecreasing:
        return index > stop
      default:
        throw new Error('Impossible')
    }
  }

  for (let index = start; comparator(index, stop); index += step) {
    yield index
  }
}

export const pairwise = <T, V>(arr: T[], map: (current: T, next: T) => V, skips = 1): V[] => {
  const result: V[] = []
  for (let i = 0; i < arr.length - skips; i++) {
    result.push(map(arr[i], arr[i + skips]))
  }
  return result
}

export const not = (value: boolean): boolean => !value

export function convertToNonNullable<T>(data: AllNullable<T>, defaultValues: T): T {
  const convertedData = structuredClone(defaultValues)
  for (const key in data) {
    const value = data[key]
    if (isDefined(value)) {
      convertedData[key] = value
    }
  }
  return convertedData
}

export const identity = <T>(value: T) => value

type NullableFunction<T1, T2> = (value: Nullable<T1>) => Nullable<T2>

export const replaceNullValue =
  <T>(defaultValue: T): NullableFunction<T, T> =>
  value =>
    value ?? defaultValue
